import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    Button,
    CircularProgress,
    Alert,
    Tooltip,
    Stack,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
    Refresh as RefreshIcon,
    Replay as RetryIcon,
    Delete as DeleteIcon,
    ErrorOutline as ErrorIcon,
    CheckCircleOutline as EmptyIcon,
} from '@mui/icons-material';
import { adminApi } from '../services/api';

const PLATFORM_COLORS = {
    instagram: '#e1306c',
    youtube:   '#ff0000',
    facebook:  '#1877f2',
    twitter:   '#1da1f2',
};

function PlatformChip({ platform }) {
    const color = PLATFORM_COLORS[platform?.toLowerCase()] || '#888';
    return (
        <Chip
            label={platform || 'unknown'}
            size="small"
            sx={{
                bgcolor: alpha(color, 0.12),
                color,
                fontWeight: 700,
                fontSize: '0.72rem',
                border: `1px solid ${alpha(color, 0.3)}`,
            }}
        />
    );
}

function TimeAgo({ iso }) {
    if (!iso) return <span>—</span>;
    const d = new Date(iso);
    const diff = Math.floor((Date.now() - d.getTime()) / 1000);
    if (diff < 60) return <span>{diff}s ago</span>;
    if (diff < 3600) return <span>{Math.floor(diff / 60)}m ago</span>;
    if (diff < 86400) return <span>{Math.floor(diff / 3600)}h ago</span>;
    return <span>{Math.floor(diff / 86400)}d ago</span>;
}

const AdminDeadLetter = () => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [actionError, setActionError] = useState(null);

    // Confirm dialog state
    const [confirmDialog, setConfirmDialog] = useState(null); // { type: 'retry'|'dismiss', taskId, commentText }

    // Per-row loading state
    const [pendingTaskId, setPendingTaskId] = useState(null);

    const fetchTasks = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const { tasks: list } = await adminApi.getDeadLetterTasks(200);
            setTasks(list || []);
        } catch (err) {
            setError(err.message || 'Failed to load dead-letter tasks');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchTasks();
    }, [fetchTasks]);

    const openConfirm = (type, taskId, commentText) =>
        setConfirmDialog({ type, taskId, commentText });
    const closeConfirm = () => setConfirmDialog(null);

    const handleConfirm = async () => {
        if (!confirmDialog) return;
        const { type, taskId } = confirmDialog;
        closeConfirm();
        setPendingTaskId(taskId);
        setActionError(null);
        try {
            if (type === 'retry') {
                await adminApi.retryDeadLetterTask(taskId);
            } else {
                await adminApi.dismissDeadLetterTask(taskId);
            }
            setTasks(prev => prev.filter(t => t.task_id !== taskId));
        } catch (err) {
            setActionError(err.message || `${type} failed`);
        } finally {
            setPendingTaskId(null);
        }
    };

    const handleRetryAll = async () => {
        if (tasks.length === 0) return;
        setLoading(true);
        setActionError(null);
        try {
            const ids = tasks.map(t => t.task_id);
            const result = await adminApi.retryDeadLetterBatch(ids);
            if (result.failed > 0) {
                setActionError(`${result.failed} task(s) could not be re-queued`);
            }
        } catch (err) {
            setActionError(err.message || 'Retry all failed');
        }
        await fetchTasks();
    };

    return (
        <Box>
            {/* Header */}
            <Box sx={{ mb: 4, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 900, mb: 0.5 }}>
                        Dead-Letter Queue
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Comment tasks that exhausted 3 auto-retries. Retry to re-queue or dismiss to delete permanently.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1.5}>
                    {tasks.length > 0 && (
                        <Button
                            variant="outlined"
                            startIcon={<RetryIcon />}
                            onClick={handleRetryAll}
                            disabled={loading}
                            size="small"
                            sx={{ fontWeight: 700, borderRadius: '10px' }}
                        >
                            Retry All ({tasks.length})
                        </Button>
                    )}
                    <Button
                        variant="outlined"
                        startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
                        onClick={fetchTasks}
                        disabled={loading}
                        size="small"
                        sx={{ fontWeight: 700, borderRadius: '10px' }}
                    >
                        Refresh
                    </Button>
                </Stack>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 3, borderRadius: '10px' }}>{error}</Alert>}
            {actionError && <Alert severity="warning" sx={{ mb: 3, borderRadius: '10px' }} onClose={() => setActionError(null)}>{actionError}</Alert>}

            <Card sx={{
                bgcolor: alpha(theme.palette.background.paper, 0.6),
                backdropFilter: 'blur(20px)',
                borderRadius: '16px',
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            }}>
                <CardContent sx={{ p: 0 }}>
                    {loading && tasks.length === 0 ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                            <CircularProgress />
                        </Box>
                    ) : tasks.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 8 }}>
                            <EmptyIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>All Clear</Typography>
                            <Typography variant="body2" color="text.secondary">No dead-letter tasks — all comments processed successfully.</Typography>
                        </Box>
                    ) : (
                        <Box sx={{ overflowX: 'auto' }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ '& th': { fontWeight: 800, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', bgcolor: alpha(theme.palette.divider, isDark ? 0.06 : 0.04), borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}` } }}>
                                        <TableCell>Platform</TableCell>
                                        <TableCell>Comment</TableCell>
                                        <TableCell>User</TableCell>
                                        <TableCell>Attempts</TableCell>
                                        <TableCell>Error</TableCell>
                                        <TableCell>Failed</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {tasks.map((task) => (
                                        <TableRow
                                            key={task.task_id}
                                            sx={{
                                                opacity: pendingTaskId === task.task_id ? 0.4 : 1,
                                                transition: 'opacity 0.2s',
                                                '& td': { borderBottom: `1px solid ${alpha(theme.palette.divider, 0.07)}`, py: 1.5 },
                                                '&:last-child td': { borderBottom: 'none' },
                                            }}
                                        >
                                            <TableCell><PlatformChip platform={task.platform} /></TableCell>
                                            <TableCell sx={{ maxWidth: 220 }}>
                                                <Tooltip title={task.comment_text || ''} arrow>
                                                    <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200, fontStyle: 'italic', color: 'text.secondary' }}>
                                                        "{task.comment_text || '—'}"
                                                    </Typography>
                                                </Tooltip>
                                                <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace', fontSize: '0.67rem' }}>
                                                    {task.task_id?.slice(0, 12)}…
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{task.username || '—'}</Typography>
                                                <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace', fontSize: '0.67rem' }}>{task.uid?.slice(0, 10)}…</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    icon={<ErrorIcon sx={{ fontSize: '14px !important' }} />}
                                                    label={task.attempts}
                                                    size="small"
                                                    sx={{ bgcolor: alpha(theme.palette.error.main, 0.1), color: 'error.main', fontWeight: 700 }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ maxWidth: 200 }}>
                                                <Tooltip title={task.error || ''} arrow>
                                                    <Typography variant="caption" color="error" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180, display: 'block' }}>
                                                        {task.error || '—'}
                                                    </Typography>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="caption" color="text.secondary">
                                                    <TimeAgo iso={task.updated_at} />
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                    <Tooltip title="Re-queue (reset attempts to 0)" arrow>
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                color="primary"
                                                                aria-label="Re-queue task"
                                                                disabled={pendingTaskId === task.task_id}
                                                                onClick={() => openConfirm('retry', task.task_id, task.comment_text)}
                                                                sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08), '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.15) } }}
                                                            >
                                                                <RetryIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                    <Tooltip title="Dismiss (delete permanently)" arrow>
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                aria-label="Dismiss task"
                                                                disabled={pendingTaskId === task.task_id}
                                                                onClick={() => openConfirm('dismiss', task.task_id, task.comment_text)}
                                                                sx={{ bgcolor: alpha(theme.palette.error.main, 0.08), '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.15) } }}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    )}
                </CardContent>
            </Card>

            {/* Confirm dialog */}
            <Dialog
                open={Boolean(confirmDialog)}
                onClose={closeConfirm}
                PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}
            >
                <DialogTitle sx={{ fontWeight: 800 }}>
                    {confirmDialog?.type === 'retry' ? 'Re-queue Task?' : 'Dismiss Task?'}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {confirmDialog?.type === 'retry'
                            ? 'This will reset attempts to 0 and put the task back in the queue. The worker will pick it up on the next poll cycle.'
                            : 'This will permanently delete the task. The comment will not be replied to.'}
                    </DialogContentText>
                    {confirmDialog?.commentText && (
                        <Box sx={{ mt: 2, p: 1.5, borderRadius: '8px', bgcolor: alpha(theme.palette.divider, 0.08), fontStyle: 'italic', fontSize: '0.85rem', color: 'text.secondary' }}>
                            "{confirmDialog.commentText}"
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={closeConfirm} sx={{ fontWeight: 700 }}>Cancel</Button>
                    <Button
                        onClick={handleConfirm}
                        variant="contained"
                        color={confirmDialog?.type === 'retry' ? 'primary' : 'error'}
                        sx={{ fontWeight: 700, borderRadius: '8px' }}
                    >
                        {confirmDialog?.type === 'retry' ? 'Re-queue' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AdminDeadLetter;
